import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { FileText, Send, MessageCircle } from 'lucide-react';
import { deviceBrands, companyInfo } from '../data/mock';

export const QuoteRequest = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    deviceType: '',
    brand: '',
    model: '',
    problem: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Récupérer l'URL du backend depuis les variables d'environnement
      const apiUrl = window.location.origin;
      
      // Envoyer les données au backend
      const response = await fetch(`${apiUrl}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi de la demande');
      }

      const result = await response.json();
      
      // Afficher un message de succès
      setShowSuccess(true);
      toast({
        title: "✅ Demande de devis envoyée !",
        description: "Nous vous contacterons rapidement. Un email de confirmation vous a été envoyé.",
        duration: 6000,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        deviceType: '',
        brand: '',
        model: '',
        problem: '',
        message: ''
      });
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite. Veuillez réessayer ou nous contacter directement.",
        variant: "destructive",
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getBrandsForDeviceType = () => {
    switch (formData.deviceType) {
      case 'smartphone':
        return deviceBrands.smartphones;
      case 'tablet':
        return deviceBrands.tablets;
      case 'computer':
        return deviceBrands.computers;
      default:
        return [];
    }
  };

  return (
    <section id="quote" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Message de succès */}
        {showSuccess && (
          <div className="mb-8 bg-green-50 border-2 border-green-500 rounded-2xl p-6 shadow-xl animate-bounce-slow">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-green-800 mb-1">✅ Demande envoyée avec succès !</h3>
                <p className="text-green-700">
                  Nous vous contacterons dans les plus brefs délais. 
                  <strong> Vérifiez votre email</strong> pour la confirmation.
                </p>
              </div>
              <button
                onClick={() => setShowSuccess(false)}
                className="text-green-600 hover:text-green-800 font-bold text-2xl"
              >×</button>
            </div>
          </div>
        )}
        
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6">
            <FileText className="text-blue-600" size={32} />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Demande de Devis Gratuit
          </h2>
          <p className="text-xl text-gray-600 mb-6">
            Obtenez un diagnostic et un devis personnalisé pour votre réparation
          </p>
          
          {/* Quick Contact Options */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <button
              onClick={() => {
                // Ouvrir directement le chatbot
                const chatbotButton = document.querySelector('button.fixed.bottom-6.left-6');
                if (chatbotButton) {
                  chatbotButton.click();
                  console.log('✅ ChatBot ouvert depuis formulaire');
                } else {
                  console.error('❌ Bouton ChatBot non trouvé');
                }
              }}
              className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-full hover:from-blue-600 hover:to-purple-600 transition-all font-semibold shadow-lg transform hover:scale-105"
            >
              <MessageCircle size={20} />
              <span>💬 Expliquez votre demande ici</span>
            </button>
            <span className="text-gray-500">ou remplissez le formulaire ci-dessous</span>
          </div>
        </div>

        {/* Form Card */}
        <Card className="border-2 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-white">
            <CardTitle className="text-2xl">Formulaire de demande</CardTitle>
            <CardDescription>
              Les prix varient selon la qualité des pièces et la disponibilité. Nous vous contacterons pour un devis précis.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom complet *</Label>
                  <Input
                    id="name"
                    placeholder="Votre nom"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    className="border-2 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="06 12 34 56 78"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    required
                    className="border-2 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  className="border-2 focus:border-blue-500"
                />
              </div>

              {/* Device Info */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  Informations sur l'appareil
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="deviceType">Type d'appareil *</Label>
                    <Select
                      value={formData.deviceType}
                      onValueChange={(value) => {
                        handleChange('deviceType', value);
                        handleChange('brand', '');
                      }}
                    >
                      <SelectTrigger className="border-2 focus:border-blue-500">
                        <SelectValue placeholder="Sélectionnez le type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="smartphone">Smartphone</SelectItem>
                        <SelectItem value="tablet">Tablette</SelectItem>
                        <SelectItem value="computer">Ordinateur</SelectItem>
                        <SelectItem value="console">Console de jeux</SelectItem>
                        <SelectItem value="smartwatch">Montre connectée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="brand">Marque</Label>
                    <Select
                      value={formData.brand}
                      onValueChange={(value) => handleChange('brand', value)}
                      disabled={!formData.deviceType}
                    >
                      <SelectTrigger className="border-2 focus:border-blue-500">
                        <SelectValue placeholder="Sélectionnez la marque" />
                      </SelectTrigger>
                      <SelectContent>
                        {getBrandsForDeviceType().map((brand) => (
                          <SelectItem key={brand} value={brand.toLowerCase()}>
                            {brand}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2 mt-6">
                  <Label htmlFor="model">Modèle de l'appareil</Label>
                  <Input
                    id="model"
                    placeholder="Ex: iPhone 14, Galaxy S23, MacBook Pro..."
                    value={formData.model}
                    onChange={(e) => handleChange('model', e.target.value)}
                    className="border-2 focus:border-blue-500"
                  />
                </div>

                <div className="space-y-2 mt-6">
                  <Label htmlFor="problem">Problème rencontré *</Label>
                  <Textarea
                    id="problem"
                    placeholder="Décrivez le problème : écran cassé, batterie HS, ne s'allume plus..."
                    value={formData.problem}
                    onChange={(e) => handleChange('problem', e.target.value)}
                    required
                    rows={3}
                    className="border-2 focus:border-blue-500"
                  />
                </div>

                <div className="space-y-2 mt-6">
                  <Label htmlFor="message">Message complémentaire (optionnel)</Label>
                  <Textarea
                    id="message"
                    placeholder="Informations supplémentaires, questions, préférences..."
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    rows={3}
                    className="border-2 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02]"
              >
                <Send className="mr-2" size={20} />
                Envoyer ma demande de devis
              </Button>

              <p className="text-sm text-gray-500 text-center">
                📞 Besoin d'un devis urgent ? Appelez-nous au <a href={`tel:${companyInfo.phoneFormatted}`} className="text-blue-600 font-semibold">{companyInfo.phone.replace(/\s/g, '')}</a>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
